/**
 * Public-route allowlist for third-party measurement.
 *
 * Third-party vendors (GA4, Google Ads, HubSpot, the Meta container) may only
 * ever observe routes that are genuinely public marketing pages. Private and
 * token-bearing routes — a customer's quote, a booking confirmation, an
 * unsubscribe link, the admin area, the partner portal, the OAuth consent
 * screen — must never reach a vendor, not even as a redacted path, because a
 * vendor's own history listener would still read the RAW url from the address
 * bar.
 *
 * The allowlist is derived from real project data, not a hand-kept copy:
 *   - every `SEO_MANIFEST` entry that is `indexable`, plus the `quote-intake`
 *     family (intake pages are public but intentionally not indexed);
 *   - `/solar-rescue/configure`;
 *   - `/service-areas/<service>/<city>` for validated service + city slugs;
 *   - `/counties/<slug>` and `/packages/<slug>` (+ `/build`) for known slugs;
 *   - the explicitly-preserved intake routes `/get-quote`, `/book-service`,
 *     `/request-construction`.
 *
 * Anything not on the list is private for measurement purposes — including
 * unknown routes and 404s. This is a VENDOR permission, layered on top of the
 * visitor's stored consent choice; the stored choice itself is never modified
 * by routing, and lead capture / form fulfilment is never blocked by it.
 */

import { SEO_MANIFEST } from "./seo-manifest";
import { CITIES, SERVICE_CATEGORIES } from "@/data/serviceAreas";
import { COUNTY_HUBS } from "@/data/counties";
import { PACKAGES } from "@/data/packages";
import { safePagePath } from "./page-context";

/** Routes that must NEVER be observed by a third-party vendor. */
const PRIVATE_PREFIXES: readonly string[] = [
  "/q/",
  "/quote/",
  "/roof-quote/",
  "/solar-rescue/confirmation/",
  "/admin",
  "/portal",
  "/partners/login",
  "/partners/signup",
  "/partners/portal",
  "/.lovable/oauth",
  "/unsubscribe",
];

const PRIVATE_EXACT: ReadonlySet<string> = new Set([
  "/booking-success",
  "/book-service/repair-success",
  "/unsubscribe",
  "/quote", // alias dispatcher — resolves to a token route before we can tell
  "/portal",
  "/admin",
]);

const ALWAYS_PUBLIC: readonly string[] = [
  "/get-quote",
  "/book-service",
  "/request-construction",
  "/visualize",
  "/request-success",
  "/solar-rescue/configure",
];

let allowlist: Set<string> | null = null;

function buildAllowlist(): Set<string> {
  const set = new Set<string>();
  for (const entry of SEO_MANIFEST) {
    if (entry.family === "private") continue;
    if (entry.indexable || entry.family === "quote-intake") set.add(entry.path);
  }
  for (const p of ALWAYS_PUBLIC) set.add(p);

  const services = new Set(SERVICE_CATEGORIES.map((s) => s.slug));
  const cities = new Set(CITIES.map((c) => c.slug));
  for (const service of services) {
    for (const city of cities) set.add(`/service-areas/${service}/${city}`);
  }
  for (const county of COUNTY_HUBS) set.add(`/counties/${county.slug}`);
  for (const pkg of PACKAGES) {
    set.add(`/packages/${pkg.slug}`);
    set.add(`/packages/${pkg.slug}/build`);
  }
  // Private entries always win, even if a manifest row disagrees.
  for (const p of PRIVATE_EXACT) set.delete(p);
  return set;
}

/** Test-only: rebuild the memoized allowlist. */
export function __resetPublicRoutesForTests(): void {
  allowlist = null;
}

function normalize(pathname: string): string {
  const clean = (pathname || "/").split(/[?#]/)[0];
  if (clean.length > 1 && clean.endsWith("/")) return clean.slice(0, -1);
  return clean || "/";
}

/** True when third-party vendors may observe this route. */
export function isPublicTrackableRoute(pathname: string): boolean {
  const path = normalize(pathname);
  if (PRIVATE_EXACT.has(path)) return false;
  for (const prefix of PRIVATE_PREFIXES) {
    if (path === prefix || path.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`)) {
      return false;
    }
  }
  if (!allowlist) allowlist = buildAllowlist();
  return allowlist.has(path);
}

/** Convenience: safe, redacted path for a route we are allowed to report. */
export function trackablePagePath(pathname: string): string | null {
  if (!isPublicTrackableRoute(pathname)) return null;
  return safePagePath(normalize(pathname));
}
